import React, { useState, useEffect } from 'react';
import { Gift, Users, TrendingUp, ArrowRight, Repeat, Share2, User, CheckCircle, Star } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

// Referral chain data outside component to avoid recreation on every render
const referralChain = [
  { name: "Sarah M.", action: "Referred 3 friends", reward: "$30", delay: 800 },
  { name: "Mike T.", action: "Referred by Sarah", reward: "$10", delay: 1600 },
  { name: "Emily R.", action: "Referred by Sarah", reward: "$10", delay: 2400 },
  { name: "James K.", action: "Referred by Mike", reward: "$10", delay: 3200 }
];

const ReferralAgent = () => {
  const [referrals, setReferrals] = useState([]);
  const [rewards, setRewards] = useState(0);

  useEffect(() => {
    if (referrals.length < referralChain.length) {
      const nextReferral = referralChain[referrals.length];
      const timer = setTimeout(() => {
        setReferrals(prev => [...prev, nextReferral]);
        setRewards(r => r + parseInt(nextReferral.reward.replace('$', '')));
      }, nextReferral.delay);
      return () => clearTimeout(timer);
    }
  }, [referrals.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50/30">
      <Header />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Referrals & Retention
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Watch customers turn into advocates—earning rewards and spreading the word automatically.
          </p>
        </div>

        {/* Animated Demo */}
        <div className="max-w-4xl mx-auto mb-12 md:mb-20">
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Stats Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-4 md:p-8">
              <div className="grid grid-cols-3 gap-2 md:gap-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl md:rounded-2xl p-2 md:p-4 text-center">
                  <Share2 className="w-4 h-4 md:w-6 md:h-6 text-white mx-auto mb-1 md:mb-2" />
                  <div className="text-lg md:text-2xl font-bold text-white">{referrals.length}</div>
                  <div className="text-[10px] md:text-xs text-green-100">Referrals</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl md:rounded-2xl p-2 md:p-4 text-center">
                  <Gift className="w-4 h-4 md:w-6 md:h-6 text-white mx-auto mb-1 md:mb-2" />
                  <div className="text-lg md:text-2xl font-bold text-white">${rewards}</div>
                  <div className="text-[10px] md:text-xs text-green-100">Rewards Earned</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl md:rounded-2xl p-2 md:p-4 text-center">
                  <TrendingUp className="w-4 h-4 md:w-6 md:h-6 text-white mx-auto mb-1 md:mb-2" />
                  <div className="text-lg md:text-2xl font-bold text-white">3x</div>
                  <div className="text-[10px] md:text-xs text-green-100">Growth</div>
                </div>
              </div>
            </div>

            {/* Referral Chain */}
            <div className="p-4 md:p-8 bg-gradient-to-b from-gray-50 to-white">
              <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4 md:mb-6 flex items-center space-x-2">
                <Users className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
                <span>Referral Chain</span>
              </h3>
              
              <div className="space-y-3 md:space-y-4">
                {referrals.map((referral, index) => (
                  <div key={index} className="animate-fadeIn">
                    <div className="bg-white border-2 border-green-200 rounded-xl md:rounded-2xl p-4 md:p-6 hover:shadow-lg transition-shadow duration-300">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center space-x-2 md:space-x-4 min-w-0">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 md:w-6 md:h-6 text-white" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm md:text-base">{referral.name}</h4>
                            <p className="text-xs md:text-sm text-gray-600 truncate">{referral.action}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                          <div className="bg-green-100 text-green-700 px-2 md:px-4 py-1 md:py-2 rounded-lg md:rounded-xl font-bold text-sm md:text-base">
                            {referral.reward}
                          </div>
                          <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-green-500" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Connection Line */}
                    {index < referrals.length - 1 && (
                      <div className="flex justify-center my-2">
                        <div className="w-0.5 h-6 bg-green-300"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {referrals.length === referralChain.length && (
                <div className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 animate-fadeIn">
                  <div className="flex items-center space-x-3">
                    <Star className="w-6 h-6 text-green-500" />
                    <div>
                      <h4 className="font-semibold text-green-900">Network Effect in Action!</h4>
                      <p className="text-sm text-green-700">Sarah's referral chain generated ${rewards} in total rewards and 4 new customers.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <Gift className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Auto Gift Cards</h3>
            <p className="text-gray-600 leading-relaxed">
              Create and distribute branded gift cards automatically
            </p>
          </div>

          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <Share2 className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Referral Programs</h3>
            <p className="text-gray-600 leading-relaxed">
              Reward customers for bringing in new business
            </p>
          </div>

          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <Repeat className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Loyalty Engine</h3>
            <p className="text-gray-600 leading-relaxed">
              Keep customers coming back with smart rewards
            </p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="text-center">
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <div className="text-4xl font-bold text-white">3x</div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">More referrals</h3>
          </div>
          <div className="text-center">
            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <Users className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Higher retention</h3>
          </div>
          <div className="text-center">
            <div className="bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <TrendingUp className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Revenue boost</h3>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-2xl p-16 shadow-xl">
          <h3 className="text-3xl font-bold text-[#2d4a1e] mb-4">
            Ready to grow through referrals?
          </h3>
          <p className="text-lg text-[#2d4a1e]/80 mb-8 max-w-2xl mx-auto">
            Start turning customers into brand advocates
          </p>
          <a
            href="https://calendly.com/kolanut"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-[#2d4a1e] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#2d4a1e]/90 transition-all duration-300 hover:scale-105 shadow-lg"
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ReferralAgent;
